import { ClockIcon } from "@heroicons/react/24/outline";
import { format, isToday, isTomorrow } from "date-fns";
import { ReactNode } from "react";

const SCHEDULE_DATE_TIME_FORMAT = "EEE MMM d h:mm a";
// The date-time format's day half.
const SCHEDULE_DAY_FORMAT = "EEE MMM d";
const SCHEDULE_SHORT_DATE_TIME_FORMAT = "MMM d h:mm a";
const SCHEDULE_SHORT_DATE_FORMAT = "MMM d";
const SCHEDULE_TIME_FORMAT = "h:mm a";
// For a date that may be from another year, like an old post's publish time.
const SCHEDULE_YEAR_DATE_TIME_FORMAT = "MMM d, yyyy, h:mm a";

export const formatScheduleDateTimeLabel = (value: Date | string | number) => {
  return format(new Date(value), SCHEDULE_DATE_TIME_FORMAT);
};

export const formatScheduleShortDateTimeLabel = (
  value: Date | string | number,
) => {
  return format(new Date(value), SCHEDULE_SHORT_DATE_TIME_FORMAT);
};

export const formatScheduleYearDateTimeLabel = (
  value: Date | string | number,
) => {
  return format(new Date(value), SCHEDULE_YEAR_DATE_TIME_FORMAT);
};

export const formatScheduleShortDateLabel = (value: Date | string | number) => {
  return format(new Date(value), SCHEDULE_SHORT_DATE_FORMAT);
};

export const formatScheduleTimeLabel = (value: Date | string | number) => {
  return format(new Date(value), SCHEDULE_TIME_FORMAT);
};

export const formatScheduleDayLabel = (value: Date | string | number) => {
  return format(new Date(value), SCHEDULE_DAY_FORMAT);
};

// The day of a sentence like "First post …": "today", "tomorrow", else
// "on Thu Oct 22" (the schedule chip's Today/Tomorrow rule).
export const formatScheduleDayPhrase = (value: Date | string | number) => {
  const date = new Date(value);
  return isToday(date) ? "today" : isTomorrow(date) ? "tomorrow" : `on ${format(date, SCHEDULE_DAY_FORMAT)}`;
};

// Completes a sentence like "First post …": "today at 11:03 PM", "tomorrow
// at 8:04 AM", else "on Thu Oct 22 at 8:04 AM".
export const formatScheduleDayTimePhrase = (value: Date | string | number) =>
  `${formatScheduleDayPhrase(value)} at ${format(new Date(value), SCHEDULE_TIME_FORMAT)}`;

type ScheduleDateLabelProps = {
  as?: "div" | "span";
  className?: string;
  icon?: ReactNode;
  iconClassName?: string;
  label: ReactNode;
};

const getClassName = (...classes: Array<string | undefined>) => {
  return classes.filter(Boolean).join(" ");
};

export default function ScheduleDateLabel({
  as = "div",
  className,
  icon,
  iconClassName,
  label,
}: ScheduleDateLabelProps) {
  const Component = as;

  return (
    <Component className={getClassName("flex items-center gap-1", className)}>
      {icon !== undefined ? (
        icon
      ) : (
        <ClockIcon
          className={getClassName("size-3.5 shrink-0", iconClassName)}
        />
      )}
      <span
        // leading-none + truncate's overflow-hidden clipped descenders
        // ("Aug"'s g); 1.2 is the minimum leading that fits them while
        // staying visually as tight.
        className="text-xs truncate whitespace-nowrap !leading-[1.2]"
      >
        {label}
      </span>
    </Component>
  );
}
